import type {
	FieldType,
	PersistedDynamicFieldDefinition,
} from "@/lib/dynamicForms/interfaces/dynamicForm.interface";
import {
	type LayoutElement,
	LayoutTypes,
} from "@/lib/layoutRenderer/schemas/layout.schema";
import {
	type CreateFormDefinitionInfo,
	EMPTY_ENTITY_DATA_VALIDATION,
	type EntityDataValidation,
	type FieldDefinition,
} from "@/services/form-definition";

const API_FIELD_KEYS = new Set([
	"id",
	"name",
	"label",
	"dataType",
	"required",
	"validationRules",
]);

function nonEmptyValidValuesStrings(
	rules: EntityDataValidation | null | undefined,
): string[] {
	const raw = rules?.validValues;
	if (!Array.isArray(raw)) return [];
	return raw.filter((v): v is string => typeof v === "string" && v.length > 0);
}

function isBooleanApiDataType(dataType: string): boolean {
	return dataType.toLowerCase() === "boolean";
}

/**
 * When `layoutInfo.fieldType` is missing, infer a valid {@link FieldType} for merged layout JSON.
 */
function resolveMergedLayoutFieldType(
	def: PersistedDynamicFieldDefinition,
	nodeFieldType: FieldType | undefined,
): FieldType {
	if (nodeFieldType !== undefined) {
		return nodeFieldType;
	}
	const vv = nonEmptyValidValuesStrings(def.validationRules);
	if (def.dataType === "text" && vv.length > 0) {
		return "select";
	}
	if (typeof def.dataType === "string" && isBooleanApiDataType(def.dataType)) {
		return "checkbox";
	}
	return def.dataType as FieldType;
}

export type FormDefinitionScopeFieldsInput = {
	jobTypeId?: string | null;
	ticketTypeId?: string | null;
	assetTypeId?: string | null;
	recordTypeId?: string | null;
};

export type FormDefinitionScopeFields = Pick<
	CreateFormDefinitionInfo,
	"jobTypeId" | "ticketTypeId" | "assetTypeId" | "recordTypeId"
>;

/**
 * Builds the entity-scope portion of POST `/form-definitions` and PUT `/form-definitions/{id}`
 * so every scope id is sent explicitly (`null` when unused).
 */
export function buildFormDefinitionScopeFields(
	input: FormDefinitionScopeFieldsInput,
): FormDefinitionScopeFields {
	return {
		jobTypeId: input.jobTypeId ?? null,
		ticketTypeId: input.ticketTypeId ?? null,
		assetTypeId: input.assetTypeId ?? null,
		recordTypeId: input.recordTypeId ?? null,
	};
}

/**
 * OpenAPI allows only these keys on {@link EntityDataValidation}.
 * Every field type sent to the API includes the object. Unused keys are null.
 */
function sanitizeValidationRules(
	rules: EntityDataValidation | null | undefined,
): EntityDataValidation {
	if (rules == null) {
		return { ...EMPTY_ENTITY_DATA_VALIDATION };
	}
	return {
		rangeMax: rules.rangeMax ?? null,
		rangeMaxExclusive: rules.rangeMaxExclusive ?? null,
		rangeMin: rules.rangeMin ?? null,
		rangeMinExclusive: rules.rangeMinExclusive ?? null,
		maxLength: rules.maxLength ?? null,
		regex: rules.regex ?? null,
		validValues: rules.validValues ?? null,
	};
}

/**
 * Maps a persisted builder definition to the POST/PUT `fieldDefinitions` row shape.
 * Strips UI-only keys (options, listeners, etc.); those are stored under
 * `layoutInfo.metadata.formEditorField` via {@link mergeFieldUiIntoLayout}.
 */
export function toApiFieldDefinition(
	def: PersistedDynamicFieldDefinition,
): FieldDefinition {
	const labelTrimmed = def.label?.trim();
	return {
		id: def.id,
		name: def.name,
		label: labelTrimmed ? labelTrimmed : null,
		dataType: def.dataType,
		required: def.required ?? false,
		validationRules: sanitizeValidationRules(def.validationRules),
	};
}

/**
 * Keys not accepted on the API `FieldDefinition` — kept in layout JSON instead.
 */
export function extractFieldUiExtras(
	def: PersistedDynamicFieldDefinition,
): Record<string, unknown> {
	const extras: Record<string, unknown> = {};
	if (def == null || typeof def !== "object") return extras;
	for (const [key, value] of Object.entries(def)) {
		if (!API_FIELD_KEYS.has(key)) extras[key] = value;
	}
	return extras;
}

const FORM_EDITOR_FIELD_METADATA_KEY = "formEditorField";

/**
 * Embeds UI-only field data into each layout `Field` node's `layoutInfo.metadata`.
 */
export function mergeFieldUiIntoLayout(
	layout: LayoutElement,
	definitions: PersistedDynamicFieldDefinition[],
): LayoutElement {
	const byId = new Map(definitions.map((d) => [d.id, d]));
	const byName = new Map(definitions.map((d) => [d.name, d]));

	function resolveDef(
		fieldDefinitionId: string,
	): PersistedDynamicFieldDefinition | undefined {
		return byId.get(fieldDefinitionId) ?? byName.get(fieldDefinitionId);
	}

	function walk(node: LayoutElement): LayoutElement {
		if (
			node.type === LayoutTypes.VerticalLayout ||
			node.type === LayoutTypes.HorizontalLayout
		) {
			return { ...node, elements: node.elements.map(walk) };
		}
		if (node.type === LayoutTypes.Group) {
			return { ...node, elements: node.elements.map(walk) };
		}
		if (node.type === LayoutTypes.Field) {
			const def = resolveDef(node.fieldDefinitionId);
			if (!def) return node;
			const extras = extractFieldUiExtras(def);
			const hasExtras = Object.keys(extras).length > 0;
			const fieldType = resolveMergedLayoutFieldType(
				def,
				node.layoutInfo?.fieldType,
			);
			if (!hasExtras && node.layoutInfo?.fieldType === fieldType) {
				return node;
			}
			return {
				...node,
				layoutInfo: {
					...node.layoutInfo,
					fieldType,
					metadata: hasExtras
						? {
								...node.layoutInfo?.metadata,
								[FORM_EDITOR_FIELD_METADATA_KEY]: extras,
							}
						: node.layoutInfo?.metadata,
				},
			};
		}
		return node;
	}

	return walk(layout);
}

/**
 * Merges `layoutInfo.metadata.formEditorField` back into definitions after loading API rows.
 */
export function enrichDefinitionsWithLayoutMetadata(
	definitions: PersistedDynamicFieldDefinition[],
	layout: LayoutElement,
): PersistedDynamicFieldDefinition[] {
	const extrasByKey = new Map<string, Record<string, unknown>>();

	function walk(node: LayoutElement): void {
		if (
			node.type === LayoutTypes.VerticalLayout ||
			node.type === LayoutTypes.HorizontalLayout
		) {
			for (const el of node.elements) walk(el);
			return;
		}
		if (node.type === LayoutTypes.Group) {
			for (const el of node.elements) walk(el);
			return;
		}
		if (node.type === LayoutTypes.Field) {
			const raw = node.layoutInfo?.metadata?.[FORM_EDITOR_FIELD_METADATA_KEY];
			if (raw && typeof raw === "object" && !Array.isArray(raw)) {
				const record = raw as Record<string, unknown>;
				extrasByKey.set(node.fieldDefinitionId, record);
			}
		}
	}

	walk(layout);

	return definitions.map((def) => {
		const extras =
			extrasByKey.get(def.id) ?? extrasByKey.get(def.name) ?? undefined;
		if (!extras) return def;
		return { ...def, ...extras } as PersistedDynamicFieldDefinition;
	});
}
